import { useLang } from "@/i18n";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";
import { openCookieSettings } from "@/components/CookieConsent";

const content = {
  es: {
    eyebrow: "Información legal",
    title: "Política de cookies",
    intro: "Esta página explica qué son las cookies, qué categorías contempla el panel de preferencias de Analytics 4 People y cómo puedes cambiar tu elección.",
    whatTitle: "¿Qué son las cookies?",
    what: "Las cookies son pequeños archivos que un sitio web puede guardar en el navegador para recordar información o facilitar funciones. También pueden utilizarse tecnologías similares, como almacenamiento local del navegador.",
    typesTitle: "Categorías de cookies",
    types: [
      ["Necesarias", "Permiten el funcionamiento básico del sitio y recordar tus preferencias de consentimiento. Siempre están activas."],
      ["Analíticas", "Ayudan a medir el uso del sitio. La herramienta de analítica de este sitio solo se carga si aceptas esta categoría."],
      ["Publicitarias", "Pueden utilizarse para gestionar espacios publicitarios."],
      ["Sociales", "Pueden permitir compartir contenido mediante plataformas sociales."],
      ["Publicidad comportamental", "Puede utilizarse para personalizar publicidad según la actividad de navegación."],
      ["Afiliados", "Pueden medir visitas procedentes de sitios afiliados."],
      ["Seguridad", "Pueden contribuir a proteger el sitio y la información almacenada."],
    ],
    choicesTitle: "Cómo gestionar tus preferencias",
    choices: "Puedes aceptar todas las categorías, rechazarlas o guardar una selección desde el panel. Tu elección se guarda en una cookie propia durante 180 días. Puedes cambiarla o retirarla en cualquier momento desde el enlace «Configurar cookies» del pie de página.",
    storageTitle: "Preferencias guardadas",
    storage: "La cookie a4p_cookie_consent almacena las categorías elegidas, la versión del panel y la fecha de actualización. No contiene datos de contacto.",
    browserTitle: "Gestionar cookies desde el navegador",
    browser: "También puedes borrar o bloquear cookies desde los ajustes de tu navegador. Si borras la cookie de preferencias, el panel volverá a mostrarse en tu próxima visita.",
    manage: "Configurar cookies",
  },
  en: {
    eyebrow: "Legal information",
    title: "Cookie policy",
    intro: "This page explains what cookies are, which categories appear in the Analytics 4 People preference panel, and how you can change your choice.",
    whatTitle: "What are cookies?",
    what: "Cookies are small files a website may store in your browser to remember information or support features. Similar technologies, such as browser local storage, may also be used.",
    typesTitle: "Cookie categories",
    types: [
      ["Necessary", "Support the basic operation of the site and remember your consent preferences. They are always active."],
      ["Analytics", "Help measure use of the site. This site's analytics tool is only loaded if you accept this category."],
      ["Advertising", "May be used to manage advertising spaces."],
      ["Social", "May enable content sharing through social platforms."],
      ["Behavioral advertising", "May be used to personalize advertising based on browsing activity."],
      ["Affiliate", "May measure visits from affiliate websites."],
      ["Security", "May help protect the site and stored information."],
    ],
    choicesTitle: "Managing your preferences",
    choices: "You can accept all categories, reject them, or save a selection in the panel. Your choice is stored in a first-party cookie for 180 days. You can change or withdraw it at any time using “Cookie settings” in the footer.",
    storageTitle: "Saved preferences",
    storage: "The a4p_cookie_consent cookie stores the selected categories, the panel version, and the date it was updated. It does not contain contact details.",
    browserTitle: "Managing cookies in your browser",
    browser: "You can also delete or block cookies in your browser settings. If you delete the preference cookie, the panel will appear again on your next visit.",
    manage: "Cookie settings",
  },
};

export default function CookiePolicy() {
  const { lang } = useLang();
  const text = content[lang] || content.es;

  return (
    <div data-testid="cookie-policy-page" className="min-h-screen bg-[#FAFAFD] text-[#353535]">
      <Navbar />
      <main className="px-5 sm:px-8 pt-36 pb-24 sm:pt-44">
        <article className="mx-auto max-w-4xl">
          <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-6">{text.eyebrow}</div>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl leading-tight">{text.title}</h1>
          <p className="mt-6 max-w-3xl text-base text-[#353535]/65 font-jakarta leading-relaxed">{text.intro}</p>
          <div className="mt-8">
            <button type="button" onClick={openCookieSettings} className="rounded-full bg-[#4361ee] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#324fd0]">{text.manage}</button>
          </div>

          <div className="mt-16 space-y-12 font-jakarta">
            <section>
              <h2 className="text-xl font-bold">{text.whatTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-[#353535]/70">{text.what}</p>
            </section>
            <section>
              <h2 className="text-xl font-bold">{text.typesTitle}</h2>
              <dl className="mt-5 divide-y divide-black/10">
                {text.types.map(([name, description]) => (
                  <div key={name} className="grid gap-2 py-4 sm:grid-cols-[220px_1fr]">
                    <dt className="text-sm font-semibold">{name}</dt>
                    <dd className="m-0 text-sm leading-6 text-[#353535]/70">{description}</dd>
                  </div>
                ))}
              </dl>
            </section>
            <section>
              <h2 className="text-xl font-bold">{text.choicesTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-[#353535]/70">{text.choices}</p>
            </section>
            <section>
              <h2 className="text-xl font-bold">{text.storageTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-[#353535]/70">{text.storage}</p>
            </section>
            <section>
              <h2 className="text-xl font-bold">{text.browserTitle}</h2>
              <p className="mt-3 text-sm leading-7 text-[#353535]/70">{text.browser}</p>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
